import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { config } from '../../../config';
import { getActiveProductLine } from '../../../adapters/AdapterRegistry';
import { AUTH_LOGOUT_FLAG_KEY } from '../../../constants/storageKeys';

export const LoginPage: React.FC = () => {
  const { authFlow, login, loginWithCredentials } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const productLabel = getActiveProductLine()?.label;

  useEffect(() => {
    if (authFlow === 'redirect') {
      const justLoggedOut = sessionStorage.getItem(AUTH_LOGOUT_FLAG_KEY);
      if (justLoggedOut) {
        sessionStorage.removeItem(AUTH_LOGOUT_FLAG_KEY);
        return;
      }
      void login();
    }
  }, [authFlow, login]);

  const handleCredentialLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await loginWithCredentials(username, password);
      navigate(config.defaultLandingPath, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  if (authFlow === 'redirect') {
    return (
      <div data-testid="login-redirecting" className="flex items-center justify-center h-screen">
        <span className="sr-only">{t('auth.redirecting', 'Redirecting…')}</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[linear-gradient(160deg,#eef1f8_0%,#f5f7fc_50%,#e8edf8_100%)]">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-[374px] rounded-2xl bg-white px-8 py-9 shadow-[0_4px_32px_0_rgba(60,80,160,0.10),0_1.5px_6px_0_rgba(60,80,160,0.06)]">

          {/* Brand */}
          <div className="flex flex-col items-center mb-5">
            <h1 className="text-[1.35rem] font-semibold text-gray-900 tracking-tight leading-tight">
              GrowthAXL
            </h1>
            {productLabel && (
              <p className="text-sm text-gray-500 mt-1">
                {t('auth.loginTo', 'Login to')}{' '}
                <span className="text-blue-500 font-medium">{productLabel}</span>
              </p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={e => void handleCredentialLogin(e)} className="space-y-4">

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('auth.username', 'Username')}
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" strokeWidth={1.8} />
                <input
                  type="text"
                  placeholder={t('auth.username', 'Username')}
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg text-sm text-gray-800 placeholder-gray-400 bg-[#fafbfd] border-[1.5px] border-[#e2e6ef] outline-none transition-colors focus:border-blue-500"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('auth.password', 'Password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" strokeWidth={1.8} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('auth.password', 'Password')}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="w-full pl-9 pr-10 py-2.5 rounded-lg text-sm text-gray-800 placeholder-gray-400 bg-[#fafbfd] border-[1.5px] border-[#e2e6ef] outline-none transition-colors focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword
                    ? <EyeOff className="w-4 h-4" strokeWidth={1.8} />
                    : <Eye className="w-4 h-4" strokeWidth={1.8} />}
                </button>
              </div>
            </div>

            {error && <p className="text-xs text-red-500">{error}</p>}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg text-sm font-semibold text-white mt-1 transition-all bg-[linear-gradient(90deg,#3b82f6_0%,#2563eb_100%)] hover:opacity-90 active:scale-[0.99] disabled:opacity-60"
            >
              {loading ? t('auth.signingIn', 'Signing in…') : t('auth.signIn', 'Sign in')}
            </button>

          </form>
        </div>
      </div>
    </div>
  );
};
